import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Leaf, Star, ArrowRight } from 'lucide-react';
import { productsAPI } from '../services/api';
import ProductCard from './ProductCard';
import Layout from './Layout';
import SEO from './SEO';

const LandingPage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await productsAPI.getAll();
        // Get top products (you can sort by bestseller or limit to first few)
        const topProducts = response.data.products.slice(0, 3);
        setProducts(topProducts);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'Wellness Coach',
      rating: 5,
      text: 'HerbalCure has transformed my daily wellness routine. The quality is exceptional and I can feel the difference!',
      avatar: 'https://ui-avatars.com/api/?name=Sarah+Johnson&background=10b981&color=fff',
    },
    {
      name: 'Michael Chen',
      role: 'Yoga Instructor',
      rating: 5,
      text: 'As someone who values natural products, I trust HerbalCure completely. Their commitment to quality is unmatched.',
      avatar: 'https://ui-avatars.com/api/?name=Michael+Chen&background=10b981&color=fff',
    },
    {
      name: 'Emma Williams',
      role: 'Nutritionist',
      rating: 5,
      text: 'I recommend HerbalCure to all my clients. Pure, effective, and sustainable - everything I look for in herbal products.',
      avatar: 'https://ui-avatars.com/api/?name=Emma+Williams&background=10b981&color=fff',
    },
  ];

  return (
    <Layout>
      <SEO 
        title="Pure Herbal Products for Natural Wellness"
        description="Discover the power of nature with Vihaan Herbals. Premium organic supplements, herbal teas, and wellness solutions for a healthier lifestyle."
        schemaData={{
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Vihaan Herbals",
          "url": "https://vihaanherbals.in",
          "logo": "https://vihaanherbals.in/logo.png",
          "description": "Premium herbal products and natural wellness solutions."
        }}
      />
      <div className=" bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-green-50 via-white to-green-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Column - Text Content */}
              <div className="space-y-8">
                {/* Badge */}
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 rounded-full text-green-700 text-sm font-medium">
                  <Leaf className="w-4 h-4" />
                  <span>100% Organic</span>
                </div>

                {/* Headline */}
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                  Nature's Gift for Your{' '}
                  <span className="text-green-600">Wellness</span>
                </h1>

                {/* Description */}
                <p className="text-lg sm:text-xl text-gray-600 leading-relaxed">
                  Discover the power of pure herbal ingredients. Our carefully crafted products bring ancient wisdom to modern wellness.
                </p>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/login"
                    className="inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-full font-medium hover:bg-green-700 transition-colors duration-200"
                  >
                    Explore Products
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                  <Link
                    to="/about"
                    className="inline-flex items-center justify-center px-6 py-3 bg-white text-green-600 border-2 border-green-600 rounded-full font-medium hover:bg-green-50 transition-colors duration-200"
                  >
                    Learn More
                  </Link>
                </div>

                {/* Statistics */}
                <div className="grid grid-cols-3 gap-6 pt-8">
                  <div>
                    <div className="text-3xl sm:text-4xl font-bold text-gray-900">100+</div>
                    <div className="text-sm sm:text-base text-gray-600 mt-1">Happy Customers</div>
                  </div>
                  <div>
                    <div className="text-3xl sm:text-4xl font-bold text-gray-900">100%</div>
                    <div className="text-sm sm:text-base text-gray-600 mt-1">Organic</div>
                  </div>
                </div>
              </div>

              {/* Right Column - Image */}
              <div className="hidden lg:block">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-green-600 rounded-3xl transform rotate-6 opacity-20"></div>
                  <div className="relative bg-white rounded-3xl shadow-2xl p-8">
                    <img
                      src="/logo.png"
                      alt="Vihaan Herbal Products"
                      className="w-full h-auto rounded-2xl"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/600x400?text=Vihaan+Herbal';
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Section */}
        <section className="py-6 sm:py-6 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Why Choose Vihaan Herbals?
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                We're committed to bringing you the finest herbal products with uncompromising quality and care.
              </p>
            </div>

            <button onClick={() => navigate('/about')} className="bg-green-600 text-white px-6 py-3 rounded-full font-medium hover:bg-green-700 transition-colors duration-200">
              Learn More
            </button>
          </div>
        </section>

        {/* Best Sellers Section */}
        <section className="py-6 sm:py-6 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Our Best Sellers
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Explore our most loved products, carefully selected for their exceptional quality and effectiveness.
              </p>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading products...</p>
              </div>
            ) : products.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    isWishlisted={false}
                    wishlistItemId={null}
                    onAddWishlist={() => navigate('/login')}
                    onRemoveWishlist={() => {}}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-600">No products available at the moment.</p>
              </div>
            )}

            <div className="text-center mt-12">
              <Link
                to="/login"
                className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-full font-medium hover:bg-green-700 transition-colors duration-200"
              >
                View All Products
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-6 sm:py-6 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                What Our Customers Say
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Join thousands of satisfied customers who have experienced the benefits of natural wellness.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-300"
                >
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star
                        key={i}
                        className="w-5 h-5 text-yellow-400 fill-yellow-400"
                      />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 italic leading-relaxed">
                    "{testimonial.text}"
                  </p>
                  <div className="flex items-center gap-3">
                    <img
                      src={testimonial.avatar}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full"
                    />
                    <div>
                      <div className="font-semibold text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-600">{testimonial.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default LandingPage;

